import { useQueryClient } from '@tanstack/react-query';
import { applyNodeChanges, type NodeChange, useReactFlow } from '@xyflow/react';
import { produce } from 'immer';
import { memo, useCallback } from 'react';

import { useTrackAgentChatEditGenerated } from '@air/analytics';
import {
  type DetectTextResult,
  type FileEditCompleteImageEdit,
  getModelSelection,
  isDetectTextResult,
  isFileEditCompleteResult,
  isGenerateTextResult,
  isImageEdit,
  isSubmitTranslationsResult,
  isVideoUrl,
  type RunLifecyclePayload,
  type SubmitTranslationsResult,
  type ToolCallResultPayload,
  useRunLifecycleSubscription,
  useSmartResizeFollowUpActionsStore,
  useToolCallResultSubscription,
} from '@air/feature-ag-ui-chat';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type Command, createGroupsForAgentGroupedEdits, useCommandHistory } from '../../hooks/commands';
import { getPrivateFontsListKey } from '../../hooks/queries/usePrivateFontsList';
import { useNodeLayout } from '../../hooks/useNodeLayout';
import { useZoomToFitIfNeeded } from '../../hooks/useZoomToFitIfNeeded';
import { useLocalizationStore } from '../../localization/localizationStore';
import { loadFontFace, parseFontVariant, registerFontKey } from '../../nodes/Text/fonts';
import { useEditCanvasStore } from '../../store';
import { useDetectTextResultStore } from '../../store/useDetectTextResultStore';
import { type AppNode, type ImageNodeData, type NodePrompt } from '../../types';
import { applySmartResizeFormatIds } from '../../utils/applySmartResizeFormatIds';
import {
  consumeTransformTargetSourceNodeId,
  resetTransformTargetSourceNodeId,
} from '../../utils/consumeTransformTargetSourceNodeId';
import { getImageMetadata, type ImageMetadata } from '../../utils/getImageMetadata';
import { getVideoMetadata, type VideoMetadata } from '../../utils/getVideoMetadata';
import { getVideoThumbnailUrl } from '../../utils/getVideoThumbnailUrl';
import { isTextEdit, type ProcessGroupedEditsResult, processGroupedEdits } from '../../utils/group/processGroupedEdits';
import { resolveInPlaceLayerEdit } from '../../utils/group/resolveInPlaceLayerEdit';
import { computePositionBesideSource } from '../../utils/layout/computePositionBesideSource';
import { computeRowAwareAppendPosition, type NodeRect } from '../../utils/layout/nodeLayout';
import { tidyNodes } from '../../utils/layout/nodeTidy';
import { preloadFontsForTextEdits } from '../../utils/preloadFontsForTextEdits';
import { splitMultiTargetFileEdit } from '../../utils/splitMultiTargetFileEdit';

type ResolvedImageEdit = {
  edit: FileEditCompleteImageEdit;
  metadata: ImageMetadata;
  type: 'image';
};

type ResolvedVideoEdit = {
  edit: FileEditCompleteImageEdit;
  metadata: VideoMetadata;
  type: 'video';
};

type ResolvedEdit = ResolvedImageEdit | ResolvedVideoEdit;

/**
 * Resolves a font family display name (e.g., "Open Sans") to its key
 * (e.g., "open-sans") using the cached fonts list. Falls back to the
 * original value if the list isn't cached or no match is found.
 */
const resolveFontKey = (queryClient: ReturnType<typeof useQueryClient>, familyName: string): string => {
  type FontsListSuccess = { data: { family: string; key: string }[] };
  const fontsListData = queryClient.getQueryData<FontsListSuccess>(getPrivateFontsListKey({ all: true }));

  return fontsListData?.data.find((f) => f.family === familyName)?.key ?? familyName;
};

type DetectTextPayload = ToolCallResultPayload & { toolCallResult: DetectTextResult };

const handleDetectTextResult = (payload: DetectTextPayload): void => {
  const { sourceNodeId, actionName } = payload.programmaticOptions ?? {};

  if (!sourceNodeId || (actionName !== 'editTextOcr' && actionName !== 'localizationOcr')) {
    return;
  }

  const node = useEditCanvasStore.getState().nodes.find((n) => n.id === sourceNodeId);
  const imageUrl = node?.type === 'image' ? node.data.media?.original || node.data.media?.image : undefined;

  if (imageUrl) {
    useDetectTextResultStore.getState().setDetectTextResult(imageUrl, payload.toolCallResult);
  }

  // Localization consumes the OCR result directly to build its review list; the source node
  // never enters the `isProcessing` spinner state (the flow stays in its own overlay). The store
  // drops the payload when it no longer matches the open translate session.
  if (actionName === 'localizationOcr') {
    useLocalizationStore.getState().applyOcrResult(sourceNodeId, payload.toolCallResult);

    return;
  }

  useEditCanvasStore.getState().updateNodeData(sourceNodeId, { isProcessing: false });
};

type SubmitTranslationsPayload = ToolCallResultPayload & { toolCallResult: SubmitTranslationsResult };

const handleSubmitTranslationsResult = (payload: SubmitTranslationsPayload): void => {
  const { actionName } = payload.programmaticOptions ?? {};

  if (actionName !== 'localizationTranslate') {
    return;
  }

  useLocalizationStore.getState().applyTranslationResult(payload.toolCallResult.targetLanguage, payload.toolCallResult);
};

/**
 * Listens for tool call results from the agent and materializes them as nodes on
 * the canvas. Handles file_edit_complete (image and video nodes, batched into one
 * undoable command), generate_text (text nodes with custom font support), and
 * submit_translations (routed into the localization store, no canvas node involved).
 */
export const ToolCallResultWatcher = memo(() => {
  const queryClient = useQueryClient();
  const execute = useCommandHistory((state) => state.execute);
  const canvasId = useEditCanvasStore((state) => state.canvasId);
  const { trackAgentChatEditGenerated } = useTrackAgentChatEditGenerated();
  const { showToast } = useToasts();
  const { getViewport } = useReactFlow();
  const { getContentNodeRects, getGap, getMaxRowWidth } = useNodeLayout();
  const { zoomToFitIfNeeded } = useZoomToFitIfNeeded();

  const handleGenerateTextResult = useCallback(
    async (payload: ToolCallResultPayload) => {
      if (!isGenerateTextResult(payload)) {
        return;
      }

      const { fontFileUrl, fontMetadataUrl, fontSource, height, image, positionX, positionY, textNode, width } =
        payload.toolCallResult;
      const fontKey = resolveFontKey(queryClient, textNode.fontFamily);

      try {
        if (fontSource === 'file' && fontFileUrl) {
          const { fontWeight, fontStyle } = parseFontVariant(textNode.fontVariant);
          registerFontKey(fontKey, textNode.fontFamily);
          await loadFontFace(textNode.fontFamily, fontFileUrl, {
            style: fontStyle,
            weight: String(fontWeight),
          });
        }

        const nodeId = crypto.randomUUID();
        const newNodeSize = { height, width };

        let position: { x: number; y: number };

        if (image) {
          const match = image.match(/^\[media:([^\]]+)\]$/);
          const imageNodeId = match?.[1];
          const imageNode = imageNodeId
            ? useEditCanvasStore.getState().nodes.find((n) => n.id === imageNodeId || n.remoteId === imageNodeId)
            : undefined;

          if (imageNode) {
            position = {
              x: imageNode.position.x + positionX,
              y: imageNode.position.y + positionY,
            };
          } else {
            // Fallback: image node not found, use standalone placement
            ({ position } = computeRowAwareAppendPosition({
              allNodes: getContentNodeRects(),
              gap: getGap(),
              maxRowWidth: getMaxRowWidth(),
              newNodeSize,
            }));
          }
        } else {
          ({ position } = computeRowAwareAppendPosition({
            allNodes: getContentNodeRects(),
            gap: getGap(),
            maxRowWidth: getMaxRowWidth(),
            newNodeSize,
          }));
        }

        const newNode: AppNode = {
          data: {
            baseWidth: width,
            fontColor: textNode.fontColor,
            fontFamily: fontKey,
            fontSize: textNode.fontSize,
            source: 'agentic-edit',
            text: textNode.text,
            textAlign: textNode.textAlign,
            type: 'text',
            ...(textNode.fontVariant ? { fontVariant: textNode.fontVariant } : {}),
            ...(textNode.lineHeight != null ? { lineHeight: textNode.lineHeight } : {}),
            ...(textNode.letterSpacing != null ? { letterSpacing: textNode.letterSpacing } : {}),
            ...(fontSource ? { fontSource } : {}),
            ...(fontFileUrl ? { fontFileUrl } : {}),
            ...(fontMetadataUrl ? { fontMetadataUrl } : {}),
          },
          id: nodeId,
          position,
          style: { height, width },
          type: 'text',
        };

        const command: Command = {
          execute: () => {
            useEditCanvasStore.getState().addNodes([newNode]);
          },
          undo: () => {
            useEditCanvasStore.getState().removeNodes([newNode.id]);
          },
        };

        execute(command);

        zoomToFitIfNeeded({ nodes: [newNode], withAnimation: true });
      } catch (error) {
        reportErrorToBugsnag({
          context: 'Failed to create text canvas node from tool result',
          error,
          metadata: {
            fontFamily: textNode.fontFamily,
            toolCallName: payload.toolCallName,
          },
        });
      }
    },
    [execute, getContentNodeRects, getGap, getMaxRowWidth, queryClient, zoomToFitIfNeeded],
  );

  const replaceImageNodeInPlace = useCallback(
    async ({
      edit,
      prompt,
      targetNodeId,
    }: {
      edit: FileEditCompleteImageEdit;
      prompt: NodePrompt;
      targetNodeId: string;
    }) => {
      const metadata = await getImageMetadata(edit.url);
      const liveNode = useEditCanvasStore.getState().nodes.find((n) => n.id === targetNodeId);

      if (liveNode?.type !== 'image') {
        return;
      }

      // Drop transient keys — keep assetId so Save as new version can restack after edits.
      const forwardData = produce(liveNode.data, (draft) => {
        delete draft.isProcessing;
        delete draft.versionId;
      });
      const previousLiveData = produce(liveNode.data, (draft) => {
        delete draft.isProcessing;
      });
      const previousData: ImageNodeData = {
        ...previousLiveData,
        fileName: liveNode.data.fileName ?? '',
        source: liveNode.data.source ?? 'manual-edit',
        type: 'image',
      };
      const previousPosition = { ...liveNode.position };
      const previousWidth = typeof liveNode.style?.width === 'number' ? liveNode.style.width : 0;
      const previousHeight = typeof liveNode.style?.height === 'number' ? liveNode.style.height : 0;

      const newData: ImageNodeData = {
        ...forwardData,
        fileName: edit.fileName ?? liveNode.data.fileName ?? '',
        media: { image: edit.url, original: edit.url },
        prompt: { messageId: prompt.messageId, runId: prompt.runId, text: prompt.text },
        source: 'agentic-edit',
        type: 'image',
      };
      // Center-anchor so the layer keeps its focal point if the edited image
      // comes back at different dimensions.
      const newPosition = {
        x: liveNode.position.x + previousWidth / 2 - metadata.width / 2,
        y: liveNode.position.y + previousHeight / 2 - metadata.height / 2,
      };

      const command: Command = {
        execute: () => {
          useEditCanvasStore.getState().updateNodeContent(targetNodeId, {
            data: newData,
            height: metadata.height,
            position: newPosition,
            width: metadata.width,
          });
        },
        undo: () => {
          useEditCanvasStore.getState().updateNodeContent(targetNodeId, {
            data: previousData,
            height: previousHeight,
            position: previousPosition,
            width: previousWidth,
          });
        },
      };

      execute(command);
    },
    [execute],
  );

  const handleToolCallResult = useCallback(
    async (
      payload: ToolCallResultPayload,
      { keepExistingSelection = false }: { keepExistingSelection?: boolean } = {},
    ) => {
      if (isDetectTextResult(payload)) {
        handleDetectTextResult(payload);

        return;
      }

      if (isSubmitTranslationsResult(payload)) {
        handleSubmitTranslationsResult(payload);

        return;
      }

      if (isGenerateTextResult(payload)) {
        return await handleGenerateTextResult(payload);
      }

      if (!isFileEditCompleteResult(payload)) {
        return;
      }

      const { edits, prompt: resultPrompt } = payload.toolCallResult;
      const { nodes } = useEditCanvasStore.getState();
      const { actionName, replaceSourceNode } = payload.programmaticOptions ?? {};
      const sourceNodeId = consumeTransformTargetSourceNodeId(payload.threadId, payload.programmaticOptions);

      // When a layer inside a group is edited (edit text, remove background,
      // prompt), replace that layer's media in place instead of appending a new
      // node — preserving its id, remoteId, and group membership so the result
      // stays in the group rather than spawning a detached copy. Crop-and-extend
      // also replaces: its intersection crop is the outpaint source and must
      // become the result instead of leaving both. Pure extend appends, like
      // translate, so the original stays on the canvas. See resolveInPlaceLayerEdit.
      const inPlace = resolveInPlaceLayerEdit({ actionName, edits, nodes, replaceSourceNode, sourceNodeId });

      if (inPlace) {
        try {
          await replaceImageNodeInPlace({
            edit: inPlace.edit,
            prompt: resultPrompt,
            targetNodeId: inPlace.targetNodeId,
          });
        } catch (error) {
          reportErrorToBugsnag({
            context: 'Failed to replace canvas node in place from tool result',
            error,
            metadata: { toolCallName: payload.toolCallName },
          });
        }

        return;
      }

      // sourceNodeId may be a remoteId, but layout rects use local node.id.
      // Resolve to the local id so targetRowNodeId matches during row lookup.
      const originNode = sourceNodeId
        ? nodes.find((n) => n.id === sourceNodeId || n.remoteId === sourceNodeId)
        : undefined;
      let resolvedTargetRowId = originNode?.id;
      // Resolve source node only for ephemeral imageGeneration nodes so they
      // get cleaned up when the AI result arrives.
      const sourceNode = originNode?.type === 'imageGeneration' ? originNode : undefined;

      // Split edits into ungrouped and grouped (positioned via group.x/y offsets)
      const ungroupedEdits = edits.filter((e) => !e.group).filter(isImageEdit);
      const groupedEdits = edits.filter((e) => e.group);

      // Phase 1: Resolve metadata for ungrouped image/video edits in parallel.
      const settled = await Promise.allSettled(
        ungroupedEdits.map(async (edit): Promise<ResolvedEdit> => {
          if (isVideoUrl(edit.url)) {
            return { edit, metadata: await getVideoMetadata(edit.url), type: 'video' };
          }

          return { edit, metadata: await getImageMetadata(edit.url), type: 'image' };
        }),
      );

      const resolved: ResolvedEdit[] = [];
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          resolved.push(result.value);
        } else {
          reportErrorToBugsnag({
            context: 'Failed to create canvas node from tool result',
            error: result.reason,
            metadata: { toolCallName: payload.toolCallName },
          });
        }
      }

      if (resolved.length === 0 && groupedEdits.length === 0) {
        return;
      }

      // Phase 2: Build AppNode objects and compute layout positions.
      const rects = getContentNodeRects();
      const maxRowWidth = getMaxRowWidth();
      const gap = getGap();
      const newNodes: AppNode[] = [];
      let replacedNode: AppNode | undefined;

      // A translation is only useful read against its original, so it goes directly beside the
      // source rather than at the end of the source's row, where unrelated nodes can separate them.
      const adjacentAnchor =
        actionName === 'translate' && resolvedTargetRowId
          ? rects.find((rect) => rect.id === resolvedTargetRowId)
          : undefined;
      // Set only once the result actually lands beside the anchor, so a boxed-in source that fell
      // back to the row append doesn't drag a distant node into the closing fitView.
      let pairedSourceNodeId: string | undefined;

      for (const [index, item] of resolved.entries()) {
        const nodeId = crypto.randomUUID();
        const newNodeSize = { height: item.metadata.height, width: item.metadata.width };

        if (index === 0 && sourceNode) {
          // Ephemeral source node (imageGeneration) — remove and append result
          replacedNode = sourceNode;

          // Remove the replaced node's rect so layout calculations
          // reflect the freed space after Phase 3 removes it.
          // First, find a row-mate to preserve row targeting after removal.
          const replacedRectIndex = rects.findIndex((r) => r.id === sourceNode.id);

          if (replacedRectIndex !== -1) {
            const sourceRect = rects[replacedRectIndex];
            const rowMate = rects.find(
              (r) => r.id !== sourceRect.id && r.y < sourceRect.y + sourceRect.height && r.y + r.height > sourceRect.y,
            );

            if (rowMate) {
              resolvedTargetRowId = rowMate.id;
            }

            rects.splice(replacedRectIndex, 1);
          }
        }

        const besideSource =
          index === 0 && adjacentAnchor
            ? computePositionBesideSource({
                allNodes: rects,
                gap,
                maxRowWidth,
                newNodeSize,
                sourceId: adjacentAnchor.id,
              })
            : undefined;

        if (besideSource?.placedBesideSource) {
          pairedSourceNodeId = adjacentAnchor?.id;
        }

        const position =
          besideSource?.position ??
          computeRowAwareAppendPosition({
            allNodes: rects,
            gap,
            maxRowWidth,
            newNodeSize,
            targetRowNodeId: resolvedTargetRowId,
          }).position;

        rects.push({
          height: item.metadata.height,
          id: nodeId,
          width: item.metadata.width,
          x: position.x,
          y: position.y,
        });

        let newNode: AppNode;

        if (item.type === 'video') {
          newNode = {
            data: {
              duration: item.metadata.duration,
              fileName: item.edit.fileName ?? '',
              media: { original: item.edit.url },
              prompt: {
                messageId: resultPrompt.messageId,
                runId: `${resultPrompt.runId}-${index}`,
                text: resultPrompt.text,
              },
              source: 'agentic-edit',
              thumbnailUrl: getVideoThumbnailUrl(item.edit.url),
              type: 'video',
              ...(item.edit.smartResizeFormatId ? { smartResizeFormatId: item.edit.smartResizeFormatId } : {}),
            },
            ...(index === 0 && replacedNode?.groupId ? { groupId: replacedNode.groupId } : {}),
            id: nodeId,
            position,
            style: { height: item.metadata.height, width: item.metadata.width },
            type: 'video',
          };
        } else {
          newNode = {
            data: {
              fileName: item.edit.fileName ?? '',
              media: { image: item.edit.url, original: item.edit.url },
              prompt: {
                messageId: resultPrompt.messageId,
                runId: `${resultPrompt.runId}-${index}`,
                text: resultPrompt.text,
              },
              source: 'agentic-edit',
              type: 'image',
              ...(item.edit.smartResizeFormatId ? { smartResizeFormatId: item.edit.smartResizeFormatId } : {}),
            },
            ...(index === 0 && replacedNode?.groupId ? { groupId: replacedNode.groupId } : {}),
            id: nodeId,
            position,
            style: { height: item.metadata.height, width: item.metadata.width },
            type: 'image',
          };
        }

        newNodes.push(newNode);
      }

      // Phase 2b: Process grouped edits (text + image layer compositions)
      const ungroupedNodeCount = newNodes.length;
      let groupResult: ProcessGroupedEditsResult | undefined;

      if (groupedEdits.length > 0) {
        // Resolve agent-provided font family names to font keys (immutable copy)
        const resolvedGroupedEdits = groupedEdits.map((edit) =>
          isTextEdit(edit)
            ? {
                ...edit,
                textNode: { ...edit.textNode, fontFamily: resolveFontKey(queryClient, edit.textNode.fontFamily) },
              }
            : edit,
        );

        const textFontInfos = resolvedGroupedEdits.filter(isTextEdit).map((edit) => ({
          fontFamily: edit.textNode.fontFamily,
          fontFileUrl: edit.textNode.fontFileUrl,
          fontSource: edit.textNode.fontSource,
          fontVariant: edit.textNode.fontVariant,
        }));

        // Preload fonts in parallel with grouped edit processing
        const [, result] = await Promise.all([
          textFontInfos.length > 0 ? preloadFontsForTextEdits(queryClient, textFontInfos) : Promise.resolve(),
          processGroupedEdits({
            gap,
            groupedEdits: resolvedGroupedEdits,
            maxRowWidth,
            prompt: resultPrompt,
            rects,
            sourceNodeId: resolvedTargetRowId,
          }),
        ]);
        groupResult = result;
        newNodes.push(...result.newNodes);
      }

      if (newNodes.length === 0) {
        return;
      }

      // Tidy newly added individual nodes so they're cleanly spaced.
      // Nodes from grouped edits are excluded — their relative positioning
      // (e.g., text at specific offsets on an image) is intentional.
      const tidyEligibleNodes = newNodes.slice(0, ungroupedNodeCount);

      if (tidyEligibleNodes.length >= 2) {
        const tidyRects: NodeRect[] = tidyEligibleNodes.map((n) => ({
          height: typeof n.style?.height === 'number' ? n.style.height : 0,
          id: n.id,
          width: typeof n.style?.width === 'number' ? n.style.width : 0,
          x: n.position.x,
          y: n.position.y,
        }));
        const tidiedPositions = tidyNodes(tidyRects, getViewport().zoom);
        const positionMap = new Map(tidiedPositions.map((p) => [p.id, p.position]));
        for (const node of tidyEligibleNodes) {
          const tidied = positionMap.get(node.id);

          if (tidied) {
            node.position = tidied;
          }
        }
      }

      if (actionName === 'videoSmartResize') {
        applySmartResizeFormatIds(newNodes, payload.programmaticOptions?.smartResizeFormatIds ?? []);
      }

      // Phase 3: Execute all node mutations as a single undoable command.
      const previousSelectedNodeIds = new Set(
        useEditCanvasStore
          .getState()
          .nodes.filter((n) => n.selected)
          .map((n) => n.id),
      );

      const command: Command = {
        execute: () => {
          const store = useEditCanvasStore.getState();

          if (replacedNode) {
            store.removeNodes([replacedNode.id]);
          }
          // Mirror commands.addNodes: ReactFlow must see selection on the first
          // store notification for drag handling; deselect others in the same set().
          store.addNodes(
            newNodes.map((n) => ({ ...n, selected: true })),
            { unselectExistingNodes: !keepExistingSelection },
          );

          // Run inside execute (not just after the first execute) so redo
          // recreates the server group after the undo's removeNodes queue
          // soft-deletes the children.
          if (groupResult?.pendingGroups.length) {
            createGroupsForAgentGroupedEdits(groupResult.pendingGroups);
          }
        },
        undo: () => {
          const store = useEditCanvasStore.getState();

          store.removeNodes(newNodes.map((n) => n.id));
          // replacedNode is always an ephemeral imageGeneration node — not
          // part of undo history, so we don't re-add it on undo.

          // Restore the selection state from before the agent results were added
          useEditCanvasStore.setState((state) => ({
            nodes: applyNodeChanges(
              state.nodes.map(
                (n): NodeChange<AppNode> => ({
                  id: n.id,
                  selected: previousSelectedNodeIds.has(n.id),
                  type: 'select',
                }),
              ),
              state.nodes,
            ),
          }));
        },
      };

      execute(command);

      // Phase 4: Side effects — track analytics.
      // Lives outside the Command because analytics aren't canvas-state mutations
      // and must not be undone/redone.
      if (canvasId) {
        const { isAuto, selectedModel } = getModelSelection(canvasId);
        for (const item of resolved) {
          trackAgentChatEditGenerated({
            canvas_id: canvasId,
            content_type: item.type === 'image' ? item.metadata.contentType : 'video',
            height: item.metadata.height,
            is_auto: isAuto,
            model_selected_manually: isAuto ? undefined : selectedModel?.name,
            thread_id: payload.threadId,
            width: item.metadata.width,
          });
        }
      }

      // Frame the source alongside the result when they were placed as a pair, so the before/after
      // reads as a comparison rather than the translation filling the viewport on its own.
      const pairedSource = pairedSourceNodeId ? nodes.filter((node) => node.id === pairedSourceNodeId) : [];

      zoomToFitIfNeeded({ nodes: [...pairedSource, ...newNodes], withAnimation: true });

      const isSmartResizeRun = actionName === 'smartResize' || actionName === 'videoSmartResize';

      if (isSmartResizeRun) {
        const variantCount = resolved.length + (groupResult?.pendingGroups.length ?? 0);

        if (variantCount > 0) {
          useSmartResizeFollowUpActionsStore.getState().setPopupRunId(resultPrompt.runId);
        }
      }
    },
    [
      canvasId,
      execute,
      getContentNodeRects,
      getGap,
      getMaxRowWidth,
      getViewport,
      handleGenerateTextResult,
      queryClient,
      replaceImageNodeInPlace,
      trackAgentChatEditGenerated,
      zoomToFitIfNeeded,
    ],
  );

  const handleRunLifecycle = useCallback(
    (payload: RunLifecyclePayload) => {
      if (payload.phase === 'started') {
        resetTransformTargetSourceNodeId(payload.threadId);
      }

      if (
        payload.phase === 'settled' &&
        payload.requestedVariantCount !== undefined &&
        payload.deliveredVariantCount > 0 &&
        payload.deliveredVariantCount < payload.requestedVariantCount
      ) {
        showToast(`${payload.deliveredVariantCount} of ${payload.requestedVariantCount} variants generated`);
      }
    },
    [showToast],
  );

  const handleToolCallResultPerTarget = useCallback(
    async (payload: ToolCallResultPayload) => {
      // Each target's results are added as their own command; later ones must not deselect the
      // earlier ones, or only the last target's results end up selected.
      for (const [index, targetPayload] of splitMultiTargetFileEdit(payload).entries()) {
        await handleToolCallResult(targetPayload, { keepExistingSelection: index > 0 });
      }
    },
    [handleToolCallResult],
  );

  useToolCallResultSubscription(handleToolCallResultPerTarget);
  useRunLifecycleSubscription(handleRunLifecycle);

  return null;
});

ToolCallResultWatcher.displayName = 'ToolCallResultWatcher';
